import React, { useState } from "react";
import { Minus, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, resolveImg } from "../../lib/api";
import { effectiveQty, isShortItem } from "../../lib/orderMath";
import SwapsSection from "./SwapsSection";
import RefundSection from "./RefundSection";

const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

function CanSendControl({ order, index, item, onUpdated }) {
  const [open, setOpen] = useState(isShortItem(item));
  const [value, setValue] = useState(effectiveQty(item));
  const [saving, setSaving] = useState(false);
  const dirty = value !== effectiveQty(item);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/orders/${order.id}/items/${index}`, { can_send: value });
      onUpdated(r.data);
      toast.success(value < item.quantity ? `Sending ${value} of ${item.quantity}` : "Sending the full amount");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-[11px] text-[var(--muted-fg)] underline hover:text-[var(--terracotta)]" data-testid={`item-short-btn-${index}`}>
        Short on this one?
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap mt-1" data-testid={`can-send-${index}`}>
      <span className="text-xs text-[var(--ink-forest)]">Can send</span>
      <div className="inline-flex items-center border border-[var(--border)] rounded-full bg-[var(--bg-cream)]">
        <button onClick={() => setValue((v) => Math.max(0, v - 1))} className="p-1.5 rounded-full hover:bg-[var(--muted)]" data-testid={`can-send-minus-${index}`}><Minus className="w-3 h-3" /></button>
        <span className="px-2 text-sm font-medium min-w-[1.5rem] text-center" data-testid={`can-send-value-${index}`}>{value}</span>
        <button onClick={() => setValue((v) => Math.min(item.quantity, v + 1))} className="p-1.5 rounded-full hover:bg-[var(--muted)]" data-testid={`can-send-plus-${index}`}><Plus className="w-3 h-3" /></button>
      </div>
      <span className="text-xs text-[var(--muted-fg)]">of {item.quantity}</span>
      {dirty && (
        <button onClick={save} disabled={saving} className="text-xs text-white bg-[var(--terracotta)] rounded-full px-3 py-1 disabled:opacity-60" data-testid={`can-send-save-${index}`}>
          Save
        </button>
      )}
    </div>
  );
}

function OfferNote({ order, onUpdated }) {
  const [note, setNote] = useState(order.offer_note || "");
  const [saving, setSaving] = useState(false);
  const dirty = note !== (order.offer_note || "");

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/orders/${order.id}/offer-note`, { note });
      onUpdated(r.data);
      toast.success("Saved. The apology note will use it");
    } catch (e) {
      toast.error(errMsg(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <label className="block" data-testid="offer-note">
      <span className="text-xs text-[var(--muted-fg)]">What I can offer instead (goes into the apology note)</span>
      <textarea
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. a similar item at the same price, or a refund for the missing ones"
        className="w-full mt-1 bg-[var(--bg-cream)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--ink-forest)] leading-relaxed focus:outline-none focus:border-[var(--terracotta)]"
        data-testid="offer-note-input"
      />
      {dirty && (
        <button type="button" onClick={save} disabled={saving} className="mt-1.5 text-xs text-white bg-[var(--terracotta)] rounded-full px-3 py-1 disabled:opacity-60" data-testid="offer-note-save">
          Save note
        </button>
      )}
    </label>
  );
}

export default function OrderItemsPanel({ order, onUpdated }) {
  const paid = order.payment_status === "paid";
  const anyShort = (order.items || []).some(isShortItem);
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-4 space-y-4" data-testid="order-items-panel">
      {order.stock_short?.length > 0 && (
        <div className="bg-[var(--terracotta)]/8 border border-[var(--terracotta)]/30 rounded-lg p-3 flex gap-2 text-xs text-[var(--ink-forest)]" data-testid="order-stock-short-banner">
          <AlertTriangle className="w-4 h-4 text-[var(--terracotta)] shrink-0" />
          <div>
            <p className="font-medium text-[var(--terracotta)]">Short on stock</p>
            {order.stock_short.map((s) => (
              <p key={s.index}>{s.name}: ordered {s.ordered}, only {s.available} were left when this was paid.</p>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="eyebrow mb-3">Items</p>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`order-item-${i}`}>
              {it.image_url && <img src={resolveImg(it.image_url)} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--ink-forest)] truncate">{it.name}</p>
                <p className="text-xs text-[var(--muted-fg)]">
                  Qty {it.quantity} × {formatPrice(it.price)}
                  {isShortItem(it) && <span className="ml-2 text-[var(--terracotta)] font-medium" data-testid={`item-sending-${i}`}>Sending {it.can_send} of {it.quantity}</span>}
                </p>
                {paid && <CanSendControl key={`${i}-${it.can_send}`} order={order} index={i} item={it} onUpdated={onUpdated} />}
              </div>
              <p className="text-sm text-[var(--ink-forest)] font-medium">{formatPrice(it.line_total)}</p>
            </div>
          ))}
        </div>
      </div>

      {paid && (anyShort || order.offer_note) && <OfferNote key={order.offer_note || ""} order={order} onUpdated={onUpdated} />}

      {paid && <SwapsSection order={order} onUpdated={onUpdated} />}

      <div className="border-t border-[var(--border)] pt-3 text-sm space-y-1">
        <div className="flex justify-between text-[var(--muted-fg)]"><span>Subtotal</span><span>{formatPrice(order.subtotal || 0)}</span></div>
        <div className="flex justify-between text-[var(--muted-fg)]"><span>Shipping</span><span>{(order.shipping_cost || 0) === 0 ? "Free" : formatPrice(order.shipping_cost || 0)}</span></div>
        {(order.discount || 0) > 0 && (
          <div className="flex justify-between text-[var(--terracotta)]"><span>Discount</span><span>−{formatPrice(order.discount)}</span></div>
        )}
        <div className="flex justify-between font-medium text-[var(--ink-forest)] pt-1 border-t border-[var(--border)] mt-1"><span>Total paid</span><span>{formatPrice(order.total || 0)}</span></div>
      </div>

      {paid && <RefundSection order={order} onUpdated={onUpdated} />}
    </div>
  );
}
